<script>
import { mapGetters } from 'vuex'
import analyticsService from '../services/analytics-service'
import { trialDays } from './../configuration'
import { isMac } from '../utils/user-agent'

export default {
    data() {
        return {
            isPanelActive: false,
            trialDays,
            searchKeyword: '',
        }
    },
    computed: {
        ...mapGetters('auth', ['loggedIn']),
        member() {
            return { ...this.$store.state.member.currentMember } || {}
        },
        isPremium() {
            return this.$store.getters['premium/isPremium']
        },
        entitledFreeTrial() {
            return !this.$store.getters['premium/hasEverBeenPremium']
        },
        routeKeyword() {
            return this.$route && this.$route.params && this.$route.params.keyword
        },
        searchPlaceholder() {
            return 'Search music'
        },
        modifierKey() {
            return isMac() ? '⌘' : 'Ctrl'
        },
    },
    watch: {
        routeKeyword: {
            immediate: true,
            handler(keyword) {
                if (keyword) {
                    const routeValue = keyword.replace(/_/g, ' ')
                    if (this.searchKeyword.trim() !== routeValue.trim()) {
                        this.searchKeyword = routeValue
                        this.$nextTick(() => {
                            this.$refs.searchInput && this.$refs.searchInput.focus()
                        })
                    }
                    this.$store.commit('search/UPDATE_KEYWORD', routeValue)
                }
            },
        },
    },
    mounted() {
        this.$keyControlsService.on('focusSearch', this.focusSearchInput)
    },
    beforeDestroy() {
        this.$keyControlsService.off('focusSearch', this.focusSearchInput)
    },
    methods: {
        focusSearchInput() {
            this.$refs.searchInput && this.$refs.searchInput.focus()
        },
        setPanelInfo(isPanelActive) {
            this.isPanelActive = isPanelActive
        },
        onPremiumUpsellClick() {
            analyticsService.premiumUpsellInitiated('top-bar')
        },
        onSearch(keyword) {
            if (keyword && keyword.trim()) {
                const urlKeyword = keyword.trim().replace(/\s+/g, '_')
                this.$store.commit('search/UPDATE_KEYWORD', keyword.trim())
                this.$router.push({ name: 'search/results/top', params: { keyword: urlKeyword } })
                this.$nextTick(() => {
                    this.$refs.searchInput && this.$refs.searchInput.focus()
                })
            }
        },
        onClearSearch() {
            this.searchKeyword = ''
        },
    },
}
</script>

<template>
    <div
        v-if="$route.name !== 'login'"
        :class="$style.headerWrapper"
    >
        <!-- Center: Search Bar -->
        <div :class="$style.searchBarWrapper">
            <BaseSearchInput
                ref="searchInput"
                v-model="searchKeyword"
                :placeholder="searchPlaceholder"
                :input-class="$style.searchInput"
                :search-icon-class="$style.searchIcon"
                :clear-icon-class="$style.clearIcon"
                @search="onSearch"
                @clear="onClearSearch"
            >
                <template #append="{ clear, hasInputValue }">
                    <BaseIcon
                        v-if="hasInputValue"
                        :class="$style.clearIcon"
                        name="x"
                        @click.native="clear"
                    />
                    <div
                        v-else
                        :class="$style.keyboardShortcut"
                    >
                        <span :class="$style.keyBadge">{{ modifierKey }}</span>
                        <span :class="$style.keyBadge">S</span>
                    </div>
                </template>
            </BaseSearchInput>
        </div>

        <!-- Right: Auth buttons OR User section -->
        <div :class="$style.rightSection">
            <!-- Non-authenticated: Sign Up / Log in -->
            <div
                v-if="!loggedIn"
                :class="$style.authButtons"
            >
                <BaseLink name="signup">
                    <BaseButton
                        :modifiers="{ noMargins: true }"
                        button-type="transparentWhite"
                        @click="() => {}"
                    >Sign Up</BaseButton>
                </BaseLink>
                <BaseLink
                    :class="$style.loginButton"
                    name="login"
                >
                    <BaseButton
                        :modifiers="{ noMargins: true }"
                        button-type="tagBlack"
                        @click="() => {}"
                    >Log in</BaseButton>
                </BaseLink>
            </div>

            <!-- Authenticated: User section -->
            <div
                v-else
                :class="$style.userDetails"
            >
                <BaseLink
                    v-if="!isPremium"
                    name="premium/view-plans"
                    :class="$style.premiumLink"
                >
                    <BaseButton
                        button-type="highlight"
                        size="xsmall"
                        :modifiers="{ fill: true, noMargins: true }"
                        @click="onPremiumUpsellClick"
                    >{{ entitledFreeTrial ? `START ${trialDays}-DAY FREE TRIAL` : `UPGRADE TO PREMIUM` }}</BaseButton>
                </BaseLink>
                <div
                    v-else
                    :class="$style.premiumLink"
                >
                    <div :class="$style.premiumContainer">
                        <BaseIcon
                            name="premium-crown"
                            :class="$style.iconPremium"
                        />
                        <span :class="$style.premiumText">PREMIUM</span>
                    </div>
                </div>
                <div :class="$style.iconContainer">
                    <BaseResourceImage
                        v-if="member.avatar"
                        :class="$style.image"
                        :should-lazyload="false"
                        :resource="member"
                        :fill="false"
                        :centered="true"
                        type="avatar"
                    />
                    <BaseIcon
                        v-else
                        :class="[$style.icon, $style.iconUser]"
                        name="user"
                    />
                </div>
                <BaseDropdown
                    :class="$style.button"
                    :padded="false"
                    @is-panel-active="setPanelInfo"
                >
                    <div slot="trigger">
                        <BaseIcon
                            :class="[$style.icon, $style.iconDropdown, isPanelActive && $style.active]"
                            name="arrow_drop_down"
                        />
                    </div>
                    <div
                        slot="dropdown"
                        :class="$style.dropdown"
                    >
                        <ul :class="$style.dropdownMenu">
                            <li :class="$style.dropdownItem">
                                <BaseLink
                                    :class="$style.link"
                                    name="settings"
                                >Settings</BaseLink>
                            </li>
                            <li :class="$style.dropdownItem">
                                <BaseLink
                                    :class="$style.link"
                                    name="account/overview"
                                    target="_blank"
                                >Account Management</BaseLink>
                            </li>
                            <div :class="$style.separator" />
                            <li :class="$style.dropdownItem">
                                <BaseLink
                                    :class="$style.link"
                                    name="logout"
                                >Log out</BaseLink>
                            </li>
                        </ul>
                    </div>
                </BaseDropdown>
            </div>
        </div>
    </div>
</template>

<style lang="scss" module>
@import '@design';

$dropdown-item-height: 44px;
$dropdown-color: $c-dusty-gray;
$avatar-size: 30px;

.headerWrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.searchBarWrapper {
    display: none;
    flex: 1;
    align-items: center;
    max-width: 600px;
    height: $size-touch-target;
    padding: 0 $s-l;
    margin: 0;
    background: $c-header-searchbar-bg;
    border-radius: $s-xl;

    @include desktop {
        display: flex;
    }
}

.searchIcon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    margin-right: $s-m;
    fill: $c-white;
}

.searchInput {
    flex: 1;
    height: 100%;
    font-size: 16px;
    font-weight: 500;
    color: $c-white;
    background: transparent;
    border: none;
    outline: none;

    &::placeholder {
        color: $c-header-searchbar-placeholder;
    }
}

.clearIcon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin-left: $s-m;
    cursor: pointer;
    opacity: 0.7;
    fill: $c-white;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 1;
    }
}

.keyboardShortcut {
    display: flex;
    align-items: center;
    gap: $s-xxs;
    margin-left: $s-m;
}

.keyBadge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    font-size: 12px;
    font-weight: 500;
    color: $c-header-searchbar-placeholder;
    border: 1px solid $c-header-searchbar-placeholder;
    border-radius: 4px;
}

.rightSection {
    display: flex;
    flex-shrink: 0;
    align-items: center;
}

.authButtons {
    display: flex;
    align-items: center;
}

.userDetails {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 0 -5px;
}

.premiumContainer {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.premiumText {
    @extend %contentSubtitleS;

    margin-top: 1px;
    font-size: 9px;
}

.image {
    width: $avatar-size;
    height: $avatar-size;
    overflow: hidden;
    border-radius: 50%;
}

.icon {
    &Container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: $avatar-size;
        height: $avatar-size;
        overflow: hidden;
        text-align: center;
        border-radius: 50%;
    }
    &User {
        position: relative;
        display: block;
        width: $avatar-size;
        height: $avatar-size;
        padding: 5px;
        background: $c-white;
        fill: $c-black;
    }
    &Premium {
        width: 27px;
        height: 27px;
        fill: $c-razzmatazz;
    }
    &Dropdown {
        width: 34px;
        height: 34px;
        fill: $c-white;
        &.active {
            transform: rotate(180deg);
        }
    }
}

.loginButton {
    margin-left: $s-m;
}

.dropdown {
    text-align: center;
    &Menu {
        padding: 0;
        margin: 0;
        list-style: none;
    }
    &Item {
        width: 100%;
        height: $dropdown-item-height;
    }
}

.link {
    @extend %contentSubtitleL;

    position: relative;
    display: block;
    line-height: $dropdown-item-height;
    color: $dropdown-color;

    &::before {
        @include fill;

        z-index: $layer-negative-z-index;
        content: '';
        background: $c-mine-shaft;
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
    }

    &:hover {
        &::before {
            opacity: 1;
        }
    }

    &:hover,
    &:active {
        text-decoration: none;
    }

    &:visited {
        color: $dropdown-color;
    }
}

.separator {
    position: relative;
    display: block;
    width: 100%;
    height: 1px;
    background: $c-mine-shaft;
}

.premiumLink {
    margin-right: $s-m;

    &:hover {
        text-decoration: none;
    }
}
</style>
